import React, { useId } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'

import {
  LntButton,
  LntDivider,
  LntForm,
  LntFormGroup,
  LntFormItem,
  LntModal,
  LntModalFooter,
  LntSelect
} from '@app/components/base'

import { SIZE } from '@app/constants'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { LOCATION_KEY, MONITORINGS_TYPES_KEY } from '@app/constants/queryKeys'

import { getLists } from '@app/api/routes/monitoringLists'
import { getTypes } from '@app/api/routes/monitorings'
import { getStores } from '@app/api/routes/locationStores'
import { getClusters } from '@app/api/routes/locationСlusters'
import { getClusterGroups } from '@app/api/routes/locationClusterGroups'
import { getFilterFromEntities } from '@app/utils/entitiesAdapters'

import { isEmptyObject, objectOmitEmptyProperties } from '@app/utils/object'

import { SEPARATOR } from '@app/constants/separator'

import styles from './styles.module.scss'

import type { LntSelectProps } from '@app/components/base/Select/types'
import type { ComplexModeFormData, ComplexModeModalProps } from './types'

const commonSelectProps: LntSelectProps = {
  mode: 'multiple',
  size: SIZE.middle,
  showArrow: true,
  strictValue: true,
  tokenSeparators: [SEPARATOR.newline]
}

export const ComplexModeModal: React.FC<ComplexModeModalProps> = ({
  title,
  submitText,
  initialMonitoringTypeIds,
  onSubmit,
  onCancel,
  ...modalProps
}) => {
  const formInstance = useForm()
  const formId = useId()

  const getListsMutation = useMutation(getLists)

  const footer = (
    <LntModalFooter>
      <LntButton
        form={ formId }
        htmlType='submit'
        disabled={ !formInstance.formState.isValid }
      >
        { submitText }
      </LntButton>
    </LntModalFooter>
  )

  const handleSubmit = async (complexModeFormData: ComplexModeFormData): Promise<void> => {
    const formData = objectOmitEmptyProperties<ComplexModeFormData>(complexModeFormData)

    if (isEmptyObject(formData)) return

    const monitoringLists = await getListsMutation.mutateAsync({ ...formData, switchLogicalOperator: true })

    if (!monitoringLists) return

    const monitoringListIds = monitoringLists.map(monitoringList => monitoringList.id)

    onSubmit(monitoringListIds)
  }

  return (
    <LntModal
      { ...modalProps }
      className={ styles.complexModeModal }
      title={ title }
      footer={ footer }
      onCancel={ onCancel }
      afterClose={ formInstance.reset }
    >
      <LntForm<ComplexModeFormData>
        preserve={ false }
        layout='vertical'
        id={ formId }
        formInstance={ formInstance }
        onSubmit={ handleSubmit }
      >
        <LntFormItem
          name='monitoringTypeIds'
          label='Выберите тип мониторинга'
          defaultValue={ initialMonitoringTypeIds ?? [] }
          validationOptions={ { rules: { required: true } } }
        >
          <LntSelect
            { ...commonSelectProps }
            disabled={ !!initialMonitoringTypeIds }
            placeholder={ FILTERS_PLACEHOLDERS.monitoringsTypeFilter }
            querySelect={ {
              queryKey: [MONITORINGS_TYPES_KEY.typesItems],
              queryFn: getTypes,
              select: getFilterFromEntities
            } }
          />
        </LntFormItem>

        <LntDivider />

        <LntFormGroup
          label='Выберите область применения'
          className={ styles.complexModeModal__applicationArea }
        >
          <LntFormItem
            name='storeIds'
            defaultValue={ [] }
          >
            <LntSelect
              { ...commonSelectProps }
              placeholder={ FILTERS_PLACEHOLDERS.storesFilter }
              querySelect={ {
                queryKey: [LOCATION_KEY.locationStoresItems],
                queryFn: getStores,
                select: getFilterFromEntities
              } }
            />
          </LntFormItem>

          <LntFormItem
            name='clusterIds'
            defaultValue={ [] }
          >
            <LntSelect
              { ...commonSelectProps }
              placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
              querySelect={ {
                queryKey: [LOCATION_KEY.locationClustersItems],
                queryFn: getClusters,
                select: getFilterFromEntities
              } }
            />
          </LntFormItem>

          <LntFormItem
            name='clustersGroupIds'
            defaultValue={ [] }
          >
            <LntSelect
              { ...commonSelectProps }
              placeholder={ FILTERS_PLACEHOLDERS.clusterGroupsFilter }
              querySelect={ {
                queryKey: [LOCATION_KEY.locationClusterGroupsItems],
                queryFn: getClusterGroups,
                select: getFilterFromEntities
              } }
            />
          </LntFormItem>
        </LntFormGroup>
      </LntForm>
    </LntModal>
  )
}
